// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { PopupContext } from "@gcg/App/Components/XPopup/XContext";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import throttle from "lodash/throttle";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

let popupIdCounter = 0;

type OwnProps = {
    trigger: JSX.Element | ((props: { isOpen: boolean }) => JSX.Element);
    parentPopup?: any;
    position?: any;
    fixed?: boolean;
    arrow?: boolean;
    shade?: boolean;
    type?: "popup" | "tooltip";
    onOpen?: (props: { popupId: number }) => void | undefined;
    onClose?: (props: { popupId: number }) => void | undefined;
    // trigger action on mouse leave, used in main navigation to open the promotion in case the mouse is still on products link
    onMouseLeave?: any;
    customWidth?: number;
    adjustOnMobile?: boolean;
} & InjectableCss;

const Popup = styled.div.attrs((props: any) => ({
    style: {
        top: `${props.position.top}px`,
        left: `${props.position.left}px`,
    },
}))<any>`
    /**
      TODO: make default style based on type, popup or tooltip
      TODO: mobile mode 
     */
    min-width: 200px;
    min-height: 150px;
    max-width: 80vw;
    height: auto;
    margin: 0;
    padding: 0;
    background-color: white;
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.2);
    position: fixed;
    z-index: ${getProp("theme.zIndex.popover")};;
    left: 10px;
    top: 10px;
    ${(props: any) => props.fixed === false && `position:absolute;`}
    opacity: 0;
    pointer-events: none;

    @media (max-width: 768px) {
        ${(props: any) =>
            props.position.left < 0 &&
            !props.position.fixed &&
            props.position.isRight &&
            `left: 10px !important; &:after, &:before{content: none !important;}`};
        ${(props: any) =>
            !props.position.isRight &&
            props.position.isOutOfBounds &&
            `left: auto !important; right: 5px !important;&:after, &:before{content: none !important;}`};
    }
  
    ${(props: any) => props.customWidth && `display: none;`};
    ${(props: any) => props.open && `opacity: 1;pointer-events: all;`};
    ${(props: any) => props.open && props.customWidth && `display: block`};
    ${(props: any) => props.isFull && `width:100%;max-width:100%;`};

    ${(props: any) =>
        props.arrow &&
        props.type !== "tooltip" &&
        css`&:before {
            content: "";
            position: absolute;
            border-width: 7px;
            border-style: solid;
            border-color: transparent transparent rgb(66, 66, 66);
            border-image: initial;
            ${(props: any) => props.position.arrowTop && `top:${props.position.arrowTop};`}
            ${(props: any) => props.position.arrowLeft && `left:${props.position.arrowLeft};`}
            ${(props: any) => props.position.transform && `transform:${props.position.transform};`}
        }`}

    ${(props: any) =>
        props.arrow &&
        props.type === "tooltip" &&
        css`
        box-shadow: 0 -3px 8px 0px rgba(0, 0, 0, 0.1);
        min-height: 50px;
        min-width: 270px;
        max-width: 330px;
        display: flex;
        justify-content: center;
        align-items: center;
          
        > div {
            padding: 10px;
            word-wrap: break-word;
            font-size: 12px;
            line-height: 18px;
        }
          
        &:before {
            content: "";
            position: absolute;
            border-style: solid;
            border-color: transparent transparent white;
            border-width: 8px;
            border-image: initial;
            ${(props: any) => props.position.arrowTop && `top:${props.position.arrowTop};`}
            ${(props: any) => props.position.arrowLeft && `left:${props.position.arrowLeft};`}
            ${(props: any) => props.position.transform && `transform:${props.position.transform};`}
        }
          
        &:after {
          content: "";
          position: absolute;
          border-style: solid;
          border-color: transparent transparent transparent;
          border-width: 8px;
          border-image: initial;
          ${(props: any) => props.position.arrowTop && `top:calc(${props.position.arrowTop} - 1px);`}
          ${(props: any) => props.position.arrowLeft && `left:${props.position.arrowLeft};`}
          ${(props: any) => props.position.transform && `transform:${props.position.transform};`}
        }`}

    ${(props: any) =>
        props.type !== "tooltip" &&
        css`
            transition: opacity 300ms ease;
        `}

    ${(props: any) =>
        props.type !== "tooltip" &&
        css`
            transition: opacity 100ms ease;
        `}

    ${injectCss}
`;

const XPopup: React.FC<OwnProps> = ({
    trigger,
    children,
    parentPopup = [],
    position = "bottom center",
    css,
    fixed = true,
    arrow = true,
    shade = false,
    type = "popup",
    onOpen,
    onClose,
    onMouseLeave,
    customWidth,
    adjustOnMobile = true,
}) => {
    let { current, setCurrent, registerOpen, registerClose } = useContext(PopupContext);
    const [popupId] = useState(++popupIdCounter);
    const contentRef = useRef<HTMLElement>(null);
    const [triggerElm, setTriggerElm] = useState<HTMLElement>({} as HTMLElement);
    const farwardRef = useCallback(node => setTriggerElm(node?.previousSibling as HTMLElement), []);

    const [positionState, setPosition] = useState<any>({ top: 0, left: 0, transform: "", arrowLeft: 0, arrowTop: 0 });

    // add triggers in case no provider as parent
    if (!setCurrent) {
        [current, setCurrent] = useState([]);
        registerOpen = (i: any) => setCurrent(i);
        registerClose = (i: any) => setCurrent(i);
    }

    const [isOpen, setIsOpen] = useState(current.includes(popupId));

    const handleCloseClick = () => {
        isOpen !== false && setIsOpen(false);
    };

    useEffect(() => {
        const newIsOpen = current.includes(popupId);
        isOpen !== newIsOpen && setIsOpen(newIsOpen);
    }, [current]);

    const pop = (
        <Popup
            open={isOpen}
            key="popup-menu"
            position={positionState}
            isFull={position.indexOf("full") !== -1}
            ref={contentRef}
            {...{ css, fixed, arrow, type }}
            onMouseLeave={onMouseLeave}
            customWidth={customWidth}
        >
            {typeof children === "function" ? children({ popupId, isOpen, handleCloseClick }) : children}
        </Popup>
    );

    const calcPosition = () => {
        const offsetY = 0;
        const offsetX = 0;
        const margin = arrow ? 10 : 0;
        const args = position.split(" ");
        const triggerBounding = triggerElm.getBoundingClientRect();
        const contentBounding: any = contentRef?.current?.getBoundingClientRect();
        const { height }: any = contentBounding;

        let width;

        if (customWidth) {
            width = customWidth;
        } else {
            width = contentBounding.width;
        }

        const CenterTop = triggerBounding.top + triggerBounding.height / 2;
        const CenterLeft = triggerBounding.left + triggerBounding.width / 2;

        let top = fixed ? CenterTop - height / 2 : 0;
        let left = fixed ? CenterLeft - width / 2 : (triggerBounding.width - width) / 2;

        let transform = "";
        let arrowTop = "0%";
        let arrowLeft = "0%";
        let isRight: boolean;

        switch (args[0]) {
            case "top":
                top -= (fixed ? height / 2 + triggerBounding.height / 2 : height) + margin;
                transform = `rotate(180deg)  translateX(50%)`;
                arrowTop = "100%";
                arrowLeft = "50%";
                break;
            case "bottom":
                top += (fixed ? height / 2 + triggerBounding.height / 2 : triggerBounding.height) + margin;
                transform = `rotate(0deg) translateY(-100%) translateX(-50%)`;
                arrowLeft = "50%";
                break;
            case "left":
                left -= (fixed ? width / 2 + triggerBounding.width / 2 : width / 2) + margin;
                transform = ` rotate(90deg)  translateY(0%) translateX(-25%)`;
                arrowLeft = "100%";
                arrowTop = "50%";
                break;
            case "right":
                left += width / 2 + triggerBounding.width / 2 + margin;
                transform = `rotate(-90deg)  translateY(-150%) translateX(25%)`;
                arrowTop = "50%";
                isRight = true;
                break;
            default:
            // TODO: custom value can be used for navigation, or simple override style
        }
        switch (args[1]) {
            case "top":
                top = (fixed ? height / 2 + triggerBounding.top / 2 : triggerBounding.top) + margin;
                arrowTop = `${triggerBounding.height / 2}px`;
                break;
            case "bottom":
                top = (fixed ? height / 2 + triggerBounding.top / 2 : triggerBounding.top) + margin;
                arrowTop = `${height - triggerBounding.height / 2}px`;
                break;
            case "left":
                left = margin - width + triggerBounding.width;
                arrowLeft = `${width - triggerBounding.width / 2}px`;
                break;
            case "right":
                left = fixed
                    ? triggerBounding.left + triggerBounding.width / 2
                    : triggerBounding.left - width - width / 2;
                arrowLeft = `${width - triggerBounding.width / 2}px`;
                isRight = true;
                break;
            case "full":
                top -= margin;
                left = 0;
                arrowLeft = `${triggerBounding.left + triggerBounding.width / 2}px`;
                break;
            default:
        }

        top = args[0] === "top" ? top - offsetY : top + offsetY;
        left = args[0] === "left" ? left - offsetX : left + offsetX;

        // check if popup is out of window and move it in
        if (["full"].indexOf(args[1]) === -1) {
            if (!fixed) {
                const outerLeft = triggerBounding.left + left + width;
                if (outerLeft > window.innerWidth) {
                    const moveAmount = outerLeft - window.innerWidth + margin + 15;
                    left -= moveAmount;
                    arrowLeft = `calc(${arrowLeft} + ${moveAmount}px)`;
                }
            } else {
                const outerLeft = left + width;
                if (outerLeft > window.innerWidth) {
                    const moveAmount = outerLeft - window.innerWidth + margin + 15;
                    left -= moveAmount;
                    arrowLeft = `calc(${arrowLeft} + ${moveAmount}px)`;
                }
            }
        }

        isRight = adjustOnMobile && (triggerBounding.left < width || triggerBounding.left < width / 2);
        const isOutOfBounds = adjustOnMobile && !fixed && left * -1 + width + triggerBounding.left > window.innerWidth;

        setPosition({ top, left, transform, arrowLeft, arrowTop, isRight, isOutOfBounds });
        return { top, left, transform, arrowLeft, arrowTop, triggerBounding, contentBounding };
    };

    const listener = throttle(() => {
        if (isOpen) {
            const { contentBounding } = calcPosition();
            if (current && isOpen && contentBounding.top < 50) {
                current.length && setCurrent([]);
            }
        }
    }, 50);

    const savedlistener = useRef<any>();
    useEffect(() => {
        savedlistener.current = listener;
    }, [listener]);

    useEffect(() => {
        const eventListener = (event: any) => savedlistener.current(event);

        window.addEventListener("resize", eventListener);
        document.addEventListener("scroll", eventListener, { passive: true });
        return () => {
            window.removeEventListener("resize", eventListener);
            document.removeEventListener("scroll", eventListener);
        };
    }, []);

    useEffect(() => {
        if (shade && current && isOpen) {
            document.querySelector("body")?.classList?.add("shade");
        } else {
            document.querySelector("body")?.classList?.remove("shade");
        }
    }, [current, isOpen]);

    useEffect(() => {
        if (current && isOpen) {
            onOpen && onOpen({ popupId });
        } else {
            onClose && onClose({ popupId });
        }
    }, [isOpen]);

    const renderTrigger = () => {
        const triggerProps: any = {
            key: "T",
            style: {},
            onMouseEnter: () => {
                registerOpen([...parentPopup, popupId]);
                calcPosition();
            },
            onMouseLeave: () => {
                registerClose(parentPopup);
            },
            /**
             * Firefox staff!
             */
            onTouchStart: () => {
                if (current?.indexOf(popupId) > -1) {
                    registerClose(parentPopup);

                    return;
                }

                registerOpen([...parentPopup, popupId]);
                calcPosition();
            },
        };

        if (!fixed) {
            triggerProps.style.position = "relative";
        }

        if (typeof trigger === "function") {
            trigger = trigger({ isOpen });
        }

        const triggerChilds = React.Children.toArray(trigger?.props?.children);
        const childs = triggerChilds.length ? [...triggerChilds, pop] : [pop];
        return !!trigger && React.cloneElement(trigger, triggerProps, childs);
    };

    return (
        <>
            {renderTrigger()}
            <span ref={farwardRef} tabIndex={-1} />
        </>
    );
};

export default XPopup;
